import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";

import "./styles.css";
import { getRouter, getQueryClient } from "./router";
import { ThemeProvider } from "./lib/theme";
import { registerAuthDeepLinkHandler } from "./lib/nativeAuth";
import { installAuthRedirectHandlers } from "./lib/authRedirects";

const router = getRouter();
const queryClient = getQueryClient();

// Capture OAuth redirects coming back into the native iOS/Android app via the
// custom URL scheme. No-op on the web.
registerAuthDeepLinkHandler();

// Forward password-recovery links (and stray OAuth fragments) to the right
// route if the email lands on `/` or another page instead of /reset-password.
installAuthRedirectHandlers();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root")!;
createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);

// Lock orientation to portrait on phones (iPhone / Android phone / phone PWA).
// Tablets (iPad / Android tablets) keep both portrait + landscape.
// iOS native lock is enforced in Info.plist via Capacitor (per-idiom).
type OrientationLock = ScreenOrientation & { lock?: (o: string) => Promise<void> };
const ua = navigator.userAgent || "";
const isIPad =
  /iPad/.test(ua) ||
  // iPadOS 13+ reports as Mac; detect via touch points.
  (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
const isAndroidTablet = /Android/.test(ua) && !/Mobile/.test(ua);
const isTablet = isIPad || isAndroidTablet;
if (!isTablet) {
  const orientation = window.screen?.orientation as OrientationLock | undefined;
  orientation?.lock?.("portrait").catch(() => {});
}

// Fade out the boot splash once React has mounted, and dismiss the native
// splash (launchAutoHide is false so it stays up until content is ready).
requestAnimationFrame(() => {
  if (Capacitor.isNativePlatform()) {
    SplashScreen.hide().catch(() => {});
  }
  const splash = document.getElementById("lovable-splash");
  if (!splash) return;
  // Minimum visible time so the logo doesn't flash on fast loads.
  window.setTimeout(() => {
    splash.classList.add("is-hiding");
    splash.addEventListener("transitionend", () => splash.remove(), { once: true });
  }, 450);
});
